import React, { useState } from "react";
import { Button, Input, PopupModal, SVG } from "../../shared";
import { Field, Formik, Form } from "formik";
import { SVG_ENUM, VALIDATION_ENUM } from "../../enums";
import * as Yup from "yup";
import { useCreateMargin, useUpdateMargin } from "../../hooks/margins";
import { MarginType } from "../../types/MarginTypes";
import "./AddEditMarginModal.scss";

interface AddEditMarginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  margin?: MarginType | null;
}

const AddEditMarginModal: React.FC<AddEditMarginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  margin = null,
}) => {
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!margin;

  const { mutate: createMargin } = useCreateMargin({
    onSuccess: () => {
      setIsSubmitting(false);
      onClose();
      setShowError(false);
      setErrorMessage("");
      onSuccess?.();
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      setShowError(true);
      setErrorMessage(
        error?.response?.data?.message || "Błąd podczas tworzenia marży"
      );
    },
  });

  const { mutate: updateMargin } = useUpdateMargin({
    onSuccess: () => {
      setIsSubmitting(false);
      onClose();
      setShowError(false);
      setErrorMessage("");
      onSuccess?.();
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      setShowError(true);
      setErrorMessage(
        error?.response?.data?.message || "Błąd podczas aktualizacji marży"
      );
    },
  });

  const validation = () =>
    Yup.object().shape({
      from: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === "" ? null : value
        )
        .min(0, "Wartość musi być większa lub równa 0"),
      to: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          String(originalValue).trim() === "" ? null : value
        )
        .min(0, "Wartość musi być większa lub równa 0")
        .test(
          "is-greater",
          'Wartość "Do" musi być większa niż "Od"',
          function (value) {
            const { from } = this.parent;
            if (from !== null && value !== null && value !== undefined) {
              return value > from;
            }
            return true;
          }
        ),
      margin: Yup.number()
        .required(VALIDATION_ENUM.REQUIRED)
        .positive("Marża musi być liczbą dodatnią")
        .min(1, "Marża musi być większa lub równa 1"),
    });

  const initialValues = {
    from: margin?.from ?? null,
    to: margin?.to ?? null,
    margin: margin?.margin || 1,
  };

  const onSubmit = (values: typeof initialValues) => {
    if (isSubmitting) return; // Prevent duplicate submissions

    setIsSubmitting(true);

    if (isEditMode && margin) {
      updateMargin({
        id: margin.id,
        from: values.from,
        to: values.to,
        margin: values.margin,
      });
    } else {
      createMargin({
        from: values.from,
        to: values.to,
        margin: values.margin,
      });
    }
  };

  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing while submitting
    setShowError(false);
    setErrorMessage("");
    setIsSubmitting(false);
    onClose();
  };

  return (
    <PopupModal isOpen={isOpen} onClose={handleClose}>
      <div className="add-edit-margin-modal">
        <div className="add-edit-margin-modal__header">
          <h2 className="popup-modal__header">
            {isEditMode ? "Edytuj marżę" : "Dodaj marżę"}
          </h2>
        </div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validation()}
          enableReinitialize={true}
        >
          {({ errors, values, handleSubmit, setFieldValue }) => (
            <Form>
              {showError && (
                <p className="add-edit-margin-modal__error">
                  {errorMessage}
                  <SVG type={SVG_ENUM.CLOSE_CIRCLE} />
                </p>
              )}

              <Field
                type="number"
                id="from"
                name="from"
                label="Od (zł)"
                placeholder="Wprowadź wartość minimalną lub zostaw puste"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <Field
                type="number"
                id="to"
                name="to"
                label="Do (zł)"
                placeholder="Wprowadź wartość maksymalną lub zostaw puste"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <Field
                type="number"
                id="margin"
                name="margin"
                label="Marża (mnożnik)"
                placeholder="Wprowadź marżę (np. 1.35)"
                step="0.01"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <div className="popup-modal__buttons">
                <Button
                  label="Anuluj"
                  styleType="stroke"
                  onClick={handleClose}
                  type="button"
                  disabled={isSubmitting}
                />
                <Button
                  label={isEditMode ? "Zaktualizuj" : "Dodaj marżę"}
                  className="button--primary"
                  styleType="primary"
                  onClick={() => handleSubmit()}
                  type="submit"
                  disabled={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PopupModal>
  );
};

export default AddEditMarginModal;
